// react
import { useCallback, useEffect, useMemo, useState } from 'react';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Alert,
} from '@mui/material';
import { Stack } from '@mui/system';
// components
import FormProvider, { BlockItem, RHFTextField } from 'src/components/hook-form';
import { enqueueSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TextMaxLine from 'src/components/text-max-line';

import { IMAGE_HOST_API } from 'src/config-global';
import { registerBorrowBook } from 'src/api/public/registerBorrowBook';
import { DatePicker } from '@mui/x-date-pickers';
import { convertToPayload, handleError } from './form-util';

type Props = {
  open: boolean;
  books: { ids: any[]; infors: any[] };
  onClose: VoidFunction;
  onSuccess?: (ids: any[]) => void;
};
export default function RegisterBorrowBookForm({ open, onClose, books, onSuccess }: Props) {
  const [apiError, setApiError] = useState<any>([]);
  const [bookData, setBookData] = useState(books);
  const Schema = Yup.object().shape({
    code: Yup.string().required('Bạn chưa nhập mã bạn đọc !'),
    receiveDate: Yup.date()
      .nullable()
      .required('Bạn chưa chọn ngày nhận ấn phẩm !')
      .typeError('Ngày nhận không hợp lệ ! ')
      .min(new Date(), 'Ngày nhận phải lớn hơn hôm nay !'),

    books: Yup.array().min(1, 'Bạn cần có ít nhất 1 ấn phẩm để đăng ký mượn !').default([]),
  });
  useEffect(() => {
    setBookData(books);
  }, [books]);
  // const bookData = useMemo(()=>{
  //   return {...books}
  // },[books])
  const defaultValues = useMemo(() => {
    console.log(bookData);
    return {
      code: '',
      receiveDate: null as any,
      books: books?.ids || [],
    };
  }, [books]);

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    reset,
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { isSubmitting, errors },
  } = methods;
  const onSubmit = handleSubmit(async (data: any) => {
    try {
      await registerBorrowBook(convertToPayload(data));
      enqueueSnackbar('Đăng ký mượn thành công !', { variant: 'success' });
      handleClose();
      if (onSuccess) {
        onSuccess(data.books);
      }
    } catch (error) {
      handleError(error, setError, setApiError);
    }
  });

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const handleClose = useCallback(() => {
    books = { ids: [], infors: [] };
    onClose();
    reset();
    setApiError([]);
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      const newBooks = getValues('books');
      const newBookData = { ...bookData };
      newBooks.splice(index, 1);
      newBookData?.ids.splice(index, 1);
      newBookData?.infors.splice(index, 1);
      setValue('books', newBooks, {
        shouldValidate: true,
      });
      setBookData({ ...newBookData });
    },
    [setValue, bookData]
  );
  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { maxWidth: 720 },
      }}
      sx={{
        '& form > *': {
          px: {
            xs: 2,
            md: 3,
          },
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>Đăng kí mượn ấn phẩm</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <BlockItem label="Mã bạn đọc : " required>
              <RHFTextField fullWidth placeholder="015248..." name="code" size="small" />
            </BlockItem>

            <BlockItem label="Ngày nhận :" required>
              <Controller
                name="receiveDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    disablePast
                    reduceAnimations
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '40px',
                      },
                    }}
                  />
                )}
              />
            </BlockItem>

            <Box>
              <Typography variant="subtitle2">Ẩn phẩm mượn : </Typography>
              {errors?.books && (
                <Alert severity="error" sx={{ my: 0.5, width: 'fit-content', mx: 'auto', py: 0 }}>
                  {`${errors.books.message}`}
                </Alert>
              )}
              <Scrollbar sx={{ maxHeight: 200 }}>
                <List dense>
                  {bookData?.infors?.map((infor: any, index: number) => (
                    <ListItem
                      key={infor.id}
                      sx={{
                        border: 1,
                        borderRadius: 1,
                        borderColor: apiError.includes(infor.id) ? 'red' : 'transparent',
                        mb: 1,
                      }}
                    >
                      {bookData?.infors.length > 1 && (
                        <ListItemIcon sx={{ mr: 0.5 }}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              handleRemove(index);
                            }}
                          >
                            <Iconify icon="carbon:close-outline" />
                          </IconButton>
                        </ListItemIcon>
                      )}
                      <ListItemText>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            alt={infor.name}
                            src={IMAGE_HOST_API + infor.photoURL}
                            variant="square"
                            sx={{ width: 50, height: 50 }}
                          />

                          <ListItemText
                            primary={
                              <TextMaxLine variant="body2" line={1}>
                                {infor.name || 'Không xác định...'}
                              </TextMaxLine>
                            }
                            secondary={
                              (
                                <TextMaxLine variant="caption" color="text.disabled" line={1}>
                                  {infor.author || 'Không xác định...'}
                                </TextMaxLine>
                              ) || 'Không xác định...'
                            }
                          />
                        </Stack>
                      </ListItemText>
                    </ListItem>
                  ))}
                </List>
              </Scrollbar>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Huỷ bỏ
          </Button>
          <LoadingButton variant="contained" color="primary" loading={isSubmitting} type="submit">
            Đăng ký mượn
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
